<template>
  <div style="max-width: 90%; margin: auto" class="py-10">
    <!-- Header -->
    <div class="d-flex justify-space-between align-center mb-8 flex-wrap" style="gap: 16px">
      <div>
        <p class="page-subtitle mb-1">Verwaltung</p>
        <h1 class="page-title">Neues Benutzerkonto anlegen</h1>
      </div>
      <button class="secondary-btn" @click="$router.push('/employees')">
        <v-icon size="16">mdi-arrow-left</v-icon>
        <span>Zurück</span>
      </button>
    </div>

    <div class="grid-split">
      <!-- Form -->
      <div class="card-modern">
        <h2 class="section-title mb-5">Benutzerdaten</h2>

        <div class="form-row">
          <div class="fg">
            <label class="fl">Vorname *</label>
            <div class="iw" :class="{ 'iw-f': focusField === 'firstname' }">
              <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-account-outline</v-icon>
              <input
                v-model="employee.firstname"
                class="ii"
                placeholder="z.B. Max"
                @focus="focusField = 'firstname'"
                @blur="focusField = null"
              />
            </div>
          </div>
          <div class="fg">
            <label class="fl">Nachname *</label>
            <div class="iw" :class="{ 'iw-f': focusField === 'lastname' }">
              <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-account-outline</v-icon>
              <input
                v-model="employee.lastname"
                class="ii"
                placeholder="z.B. Mustermann"
                @focus="focusField = 'lastname'"
                @blur="focusField = null"
              />
            </div>
          </div>
        </div>

        <div class="fg mb-4">
          <label class="fl">E-Mail *</label>
          <div class="iw" :class="{ 'iw-f': focusField === 'email' }">
            <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-email-outline</v-icon>
            <input
              v-model="employee.email"
              type="email"
              class="ii"
              placeholder="z.B. max.mustermann@example.com"
              @focus="focusField = 'email'"
              @blur="focusField = null"
            />
          </div>
        </div>

        <div class="fg mb-4">
          <label class="fl">Position *</label>
          <div class="iw" :class="{ 'iw-f': focusField === 'position' }">
            <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-briefcase-outline</v-icon>
            <input
              v-model="employee.position"
              class="ii"
              placeholder="z.B. Entwickler, Designer, Manager"
              @focus="focusField = 'position'"
              @blur="focusField = null"
            />
          </div>
        </div>

        <div v-if="errorMessage" class="alert-error mt-4">
          <v-icon size="16" color="#D32F2F">mdi-alert-circle-outline</v-icon>{{ errorMessage }}
        </div>

        <button class="submit-btn mt-6" :disabled="!canSubmit" @click="createEmployee">
          <v-progress-circular v-if="submitting" indeterminate size="14" width="2" color="white" />
          <v-icon v-else size="16">mdi-account-plus-outline</v-icon>
          <span>Benutzerkonto erstellen</span>
        </button>
      </div>

      <!-- Avatar -->
      <div class="card-modern avatar-side">
        <h2 class="section-title mb-4">
          <v-icon size="18" color="#0a1628" class="mr-2">mdi-account-circle-outline</v-icon>
          Avatar
        </h2>

        <input
          ref="fileInput"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          style="display: none"
          @change="onFileSelected"
        />

        <div class="upload-box" @click="triggerFileDialog">
          <v-img v-if="imagePreview" :src="imagePreview" cover aspect-ratio="1" />
          <div v-else class="upload-placeholder">
            <v-icon size="44" color="#c5cbd3" class="mb-3">mdi-camera-outline</v-icon>
            <div class="upload-title">Avatar hochladen</div>
            <div class="upload-sub">Klicke zum Auswählen</div>
            <div class="upload-hint">Max. 5 MB · PNG, JPG, WEBP</div>
          </div>
        </div>

        <button v-if="imagePreview" class="bs mt-3" @click="triggerFileDialog">
          <v-icon size="15">mdi-image-edit-outline</v-icon>
          Avatar ändern
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'EmployeeCreate',
  data() {
    return {
      employee: { firstname: '', lastname: '', email: '', position: '', imageFile: null },
      imagePreview: null,
      submitting: false,
      errorMessage: '',
      focusField: null,
    };
  },

  computed: {
    canSubmit() {
      const e = this.employee;
      return e.firstname && e.lastname && e.email && e.position && !this.submitting;
    },
  },

  methods: {
    async createEmployee() {
      if (!this.canSubmit) return;
      this.errorMessage = '';
      const fd = new FormData();
      fd.append('firstname', this.employee.firstname);
      fd.append('lastname', this.employee.lastname);
      fd.append('email', this.employee.email);
      fd.append('position', this.employee.position);
      if (this.employee.imageFile) fd.append('avatar', this.employee.imageFile);

      this.submitting = true;
      try {
        const result = await axios.post('/api/employees/create', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (result?.data?.inserted) this.$router.push('/employees');
      } catch (e) {
        console.error(e);
        this.errorMessage = e.response?.data?.message || 'Fehler beim Erstellen des Benutzers';
      } finally {
        this.submitting = false;
      }
    },

    triggerFileDialog() { this.$refs.fileInput.click(); },

    onFileSelected(e) {
      const file = e.target.files?.[0] || null;
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        this.errorMessage = 'Die Datei ist größer als 5 MB.';
        return;
      }
      this.employee.imageFile = file;
      const reader = new FileReader();
      reader.onload = () => (this.imagePreview = reader.result);
      reader.readAsDataURL(file);
    },
  },
};
</script>

<style scoped>
.page-title { font-size: 26px; font-weight: 800; color: #0a1628; letter-spacing: -0.3px; margin-bottom: 6px; }
.page-subtitle { font-size: 13px; color: #888; }

.secondary-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 18px; background: white; color: #0a1628;
  border: 1px solid #eceef2; border-radius: 10px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: border-color 0.15s; font-family: inherit;
}
.secondary-btn:hover { border-color: #0a1628; }

.grid-split { display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); gap: 20px; align-items: start; }

.card-modern { background: white; border: 1px solid #eceef2; border-radius: 16px; padding: 28px; }
.section-title {
  font-size: 16px; font-weight: 700; color: #0a1628;
  display: flex; align-items: center;
}

.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 16px; }

.fg { display: flex; flex-direction: column; }
.fg.mb-4 { margin-bottom: 16px; }
.fl { font-size: 11px; font-weight: 600; color: #999; margin-bottom: 6px; letter-spacing: 0.3px; }

.iw {
  display: flex; align-items: center; gap: 10px;
  height: 42px; padding: 0 14px;
  background: #f8f9fb; border: 1px solid #eceef2; border-radius: 10px;
  transition: all 0.15s;
}
.iw.iw-f { border-color: #0a1628; background: white; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.ii {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: 14px; color: #0a1628; font-weight: 500; min-width: 0;
  font-family: inherit; height: 40px;
}
.ii::placeholder { color: #bbb; font-weight: 400; }

.submit-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 14px 20px;
  background: #2E7D32; color: white; border: none; border-radius: 12px;
  font-size: 14px; font-weight: 700; cursor: pointer;
  transition: background 0.15s; font-family: inherit;
}
.submit-btn:hover:not(:disabled) { background: #1B5E20; }
.submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.alert-error {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; background: rgba(211,47,47,0.08); color: #D32F2F;
  border-radius: 10px; font-size: 13px; font-weight: 500;
}

/* Avatar side */
.avatar-side { position: sticky; top: 24px; }
.upload-box {
  position: relative; background: #f8f9fb; border: 1px dashed #c5cbd3; border-radius: 12px;
  overflow: hidden; cursor: pointer; transition: all 0.15s;
}
.upload-box:hover { border-color: #0a1628; background: white; }
.upload-placeholder {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 280px; padding: 24px; text-align: center;
}
.upload-title { font-size: 14px; font-weight: 700; color: #0a1628; margin-bottom: 2px; }
.upload-sub { font-size: 12px; color: #888; }
.upload-hint { font-size: 11px; color: #aaa; margin-top: 6px; }

.bs {
  display: inline-flex; align-items: center; gap: 8px; width: 100%; justify-content: center;
  padding: 10px 14px; border: 1px dashed #c5cbd3; border-radius: 10px; background: #fafbfc;
  font-size: 13px; font-weight: 600; color: #555; cursor: pointer; transition: all 0.15s;
  font-family: inherit;
}
.bs:hover { border-color: #0a1628; color: #0a1628; background: white; }

@media (max-width: 960px) {
  .grid-split { grid-template-columns: 1fr; }
  .avatar-side { position: static; }
  .form-row { grid-template-columns: 1fr; }
}
</style>
